// Shared by Cloud Functions and the frontend (imported there as @shared).
// Pro trades: when they're allowed, draft pick names and the rules a trade
// must pass (league/trades.js has the server side).
//
// summary/trades: {picks: {pickId: ownerTeamId} (only picks that changed
// hands), offers: {offerId: offer}, log: [completed trades, newest first]}.
// A pick id is "<draft year>-<round>-<original team id>"; the draft year
// is the season its rookies play (draft.year in league/draft.js).
// offer: {id, fromTeamId, toTeamId, give: {players: [ids], picks: [ids]},
// get: {players, picks}, briefs: {playerId: brief}, status ("pending",
// "accepted", "rejected", "withdrawn", "expired", "failed"), clock,
// createdAt, cpu (a computer team's offer), reply}.

import {capYear, payroll} from "./contracts.js";
import {DRAFT_ROUNDS} from "./draft.js";

// The trade deadline: this share of the regular season's days.
export const DEADLINE_SHARE = 2 / 3;
// Draft years that can be traded: the next draft and the one after.
export const PICK_YEARS = 2;
// A pending offer lapses after this many days.
export const OFFER_DAYS = 3;
export const MAX_OUTGOING = 5;
// Offers a manager can make to computer-run teams a day.
export const CPU_ASKS_PER_DAY = 3;
export const OFFSEASON_MIN_ROSTER = 8;
// Offseason stages when trading is open: after the draft, through free
// agency.
const OFFSEASON_TRADE = /^fa-/;

/**
 * The last regular-season day trades are allowed.
 * @param {object} league
 * @return {number}
 */
export function deadlineDay(league) {
  return Math.ceil((league.season.totalDays ?? 0) * DEADLINE_SHARE);
}

/**
 * Whether trades are allowed now: pro leagues in the preseason, the
 * regular season up to the deadline, and free agency.
 * @param {object} league
 * @return {boolean}
 */
export function tradeWindowOpen(league) {
  const s = league?.season;
  if (league?.type !== "pro" || !s) return false;
  if (s.phase === "preseason") return true;
  if (s.phase === "regular") return (s.day ?? 0) < deadlineDay(league);
  return s.phase === "offseason" &&
    OFFSEASON_TRADE.test(s.offseason?.stage ?? "");
}

/**
 * A number that grows by one each sim step (game day or offseason
 * stage), for offers that lapse.
 * @param {object} league
 * @return {number}
 */
export function tradeClock(league) {
  const s = league.season;
  const year = s.year ?? 1;
  if (s.phase === "offseason") {
    const m = /^fa-(\d+)$/.exec(s.offseason?.stage ?? "");
    const stage = s.offseason?.stage === "fa-open" ? 1 :
      m ? Number(m[1]) : s.offseason?.stage === "fa-close" ? 9 : 0;
    return year * 1000 + 500 + stage;
  }
  return year * 1000 + (s.day ?? 0);
}

/**
 * The next draft's year (its rookies' first season): the draft after
 * season N is labeled N + 1. Once this offseason's draft is held, the
 * one after.
 * @param {object} league
 * @param {object|null} [draft] summary/draft
 * @return {number}
 */
export function nextDraftYear(league, draft = null) {
  const year = (league.season.year ?? 1) + 1;
  return draft?.year === year && draft.done ? year + 1 : year;
}

/**
 * @param {number} year
 * @param {number} round
 * @param {string} teamId the original team
 * @return {string}
 */
export function pickId(year, round, teamId) {
  return `${year}-${round}-${teamId}`;
}

/**
 * @param {string} id
 * @return {object} {year, round, teamId}
 */
export function parsePick(id) {
  const [year, round, ...rest] = String(id).split("-");
  return {year: Number(year), round: Number(round), teamId: rest.join("-")};
}

/**
 * The draft picks a team can trade: every tradable pick it owns.
 * @param {string} teamId
 * @param {string[]} teamIds every team in the league
 * @param {object} owners summary/trades picks {pickId: owner}
 * @param {number} firstYear nextDraftYear
 * @return {string[]} pick ids, soonest first
 */
export function ownedPicks(teamId, teamIds, owners, firstYear) {
  const out = [];
  for (let y = firstYear; y < firstYear + PICK_YEARS; y++) {
    for (let round = 1; round <= DRAFT_ROUNDS; round++) {
      for (const original of [...teamIds].sort()) {
        const id = pickId(y, round, original);
        if ((owners?.[id] ?? original) === teamId) out.push(id);
      }
    }
  }
  return out;
}

/**
 * "Season 3 1st-round pick (BOS)".
 * @param {string} id
 * @param {object} [teams] {teamId: {abbr}}
 * @return {string}
 */
export function pickLabel(id, teams = {}) {
  const {year, round, teamId} = parsePick(id);
  const abbr = teams?.[teamId]?.abbr ?? teamId;
  return `Season ${year} ${round === 1 ? "1st" : "2nd"}-round pick (${abbr})`;
}

/**
 * What's wrong with a trade, if anything: both teams must own what they
 * send, stay within roster limits and end up under the cap (or no further
 * over it than before).
 * @param {object} args
 * @param {object} args.league
 * @param {object} args.from the proposing team (document)
 * @param {object} args.to the other team
 * @param {object} args.give {players, picks} from sends
 * @param {object} args.get {players, picks} to sends
 * @param {object} args.owners pick owners
 * @param {string[]} args.tradable pick ids that can be traded now
 * @param {object} args.rules moneyRules
 * @return {string[]} problems (empty when the trade is allowed)
 */
export function tradeProblems({league, from, to, give, get, owners,
  tradable, rules}) {
  const problems = [];
  if (!tradeWindowOpen(league)) {
    problems.push(league.season.phase === "regular" ?
      "The trade deadline has passed." :
      "Trades are closed until free agency opens.");
  }
  if (!from || !to || from.id === to.id) {
    problems.push("Pick another team to trade with.");
    return problems;
  }
  const count = (side) => side.players.length + side.picks.length;
  if (!count(give) && !count(get)) problems.push("The trade is empty.");
  const year = capYear(league);
  const offseason = league.season.phase === "offseason";
  for (const [team, side] of [[from, give], [to, get]]) {
    for (const pid of side.players) {
      const p = team.players[pid];
      if (!p) {
        problems.push(`A player in the trade is no longer with ${team.abbr}.`);
      } else if (offseason && p.contract?.years === 0) {
        problems.push(`${p.fName} ${p.lName}'s contract has run out.`);
      }
    }
    for (const id of side.picks) {
      if (!tradable.includes(id) || (owners?.[id] ??
        parsePick(id).teamId) !== team.id) {
        problems.push(`${team.abbr} doesn't have that pick to trade.`);
      }
    }
  }
  if (problems.length) return problems;
  for (const [team, out, inn] of [[from, give, get], [to, get, give]]) {
    const other = team === from ? to : from;
    const size = Object.keys(team.players).length - out.players.length +
      inn.players.length;
    const min = offseason ? OFFSEASON_MIN_ROSTER : rules.rosterMin;
    if (size > rules.rosterMax) {
      problems.push(`${team.abbr} would have ${size} players ` +
        `(the most is ${rules.rosterMax}).`);
    }
    if (size < min) {
      problems.push(`${team.abbr} would have ${size} players ` +
        `(the fewest is ${min}).`);
    }
    const before = payroll(team, year);
    const after = before - salaries(team, out.players) +
      salaries(other, inn.players);
    if (after > rules.cap && after > before) {
      problems.push(`${team.abbr} would be over the cap.`);
    }
  }
  return problems;
}

/**
 * Payroll after a trade, for showing it.
 * @param {object} team
 * @param {object} other the trade partner
 * @param {string[]} out player ids leaving
 * @param {string[]} inn player ids arriving (from other)
 * @param {number} year cap year
 * @return {number}
 */
export function payrollAfter(team, other, out, inn, year) {
  return payroll(team, year) - salaries(team, out) + salaries(other, inn);
}

/**
 * @param {object} team
 * @param {string[]} ids
 * @return {number} the players' salaries (expiring contracts count 0)
 */
function salaries(team, ids) {
  return ids.reduce((n, pid) => {
    const c = team?.players?.[pid]?.contract;
    return n + (c && c.years > 0 ? c.salary : 0);
  }, 0);
}
